"use client";

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { CircleCheck, CircleAlert, RefreshCw, Search, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import logger from "@/lib/logger";

const USD_TO_CNY = 7;

function detectLocale(pathname: string | null): string {
  if (!pathname) return "zh";
  const seg = pathname.split("/")[1];
  return ["en", "zh", "ja"].includes(seg) ? seg : "zh";
}

function formatPrice(usd: number, locale: string, decimals: number): string {
  if (locale === "zh") {
    return `¥${(usd * USD_TO_CNY).toFixed(decimals)}`;
  }
  return `$${usd.toFixed(decimals)}`;
}

interface EndpointInfo {
  path: string;
  method: string;
}

interface BillingItem {
  id: string;
  modelName: string;
  billingType: string;
  inputPrice: string;
  outputPrice: string;
  inputRate: string;
  outputRate: string;
  group: string;
  fallbackModel: string;
  description: string;
  cachePromptPrice: string;
  cacheCreationPrice: string;
  supportedEndpointTypes: string[];
}

export default function BillingContent() {
  const t = useTranslations("billing");
  const pathname = usePathname();
  const locale = detectLocale(pathname);
  const [billingItems, setBillingItems] = useState<BillingItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [endpointMap, setEndpointMap] = useState<Record<string, EndpointInfo>>({});

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // 过滤后的数据
  const filteredItems = billingItems.filter(item =>
    item.modelName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 获取并处理后端价格数据
  const fetchPricing = async () => {
    try {
      setIsRefreshing(true);

      // 仅向本地代理路由请求，本地代理会在上游缺失时返回 mock 数据以避免 CORS 错误
      const res = await fetch("/api/pricing/models");

      if (!res.ok) throw new Error("fetch_failed");
      const raw = await res.json();

      // 提取全局 supported_endpoint 映射
      if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        const sep = (raw as Record<string, unknown>)["supported_endpoint"];
        if (sep && typeof sep === "object") {
          setEndpointMap(sep as Record<string, EndpointInfo>);
        }
      }

      // 正常化返回结构：支持直接返回数组，或 { data: [...] }，或 { models: [...] }
      let dataArray: unknown[] = [];
      if (Array.isArray(raw)) {
        dataArray = raw as unknown[];
      } else if (raw && Array.isArray((raw as Record<string, unknown>).data)) {
        dataArray = (raw as Record<string, unknown>).data as unknown[];
      } else if (raw && Array.isArray((raw as Record<string, unknown>).models)) {
        dataArray = (raw as Record<string, unknown>).models as unknown[];
      } else {
        logger.warn("/api/pricing/models 返回非数组结构，已忽略内容：%o", raw);
        dataArray = [];
      }

      // 将后端数据映射为页面展示结构
      const items: BillingItem[] = dataArray.map((m: unknown) => {
        const mm = (m || {}) as Record<string, unknown>;
        const id = `${String(mm["model_name"] ?? "")}__${String(mm["vendor_id"] ?? "0")}`;
        const modelName = typeof mm["model_name"] === "string" ? (mm["model_name"] as string) : "-";
        const enableGroups = Array.isArray(mm["enable_groups"])
          ? (mm["enable_groups"] as unknown[])
          : [];
        const group = enableGroups.length > 0 ? (enableGroups as string[]).join(",") : "default";

        const fallbackModel = typeof mm["fallback_model"] === "string" ? (mm["fallback_model"] as string) : "-";
        const description = typeof mm["description"] === "string" ? (mm["description"] as string) : "-";
        const cachePromptPrice = typeof mm["cache_prompt_price"] === "number"
          ? formatPrice(mm["cache_prompt_price"] as number, locale, 6)
          : "-";
        const cacheCreationPrice = typeof mm["cache_creation_price"] === "number"
          ? formatPrice(mm["cache_creation_price"] as number, locale, 6)
          : "-";
        const supportedEndpointTypes = Array.isArray(mm["supported_endpoint_types"])
          ? (mm["supported_endpoint_types"] as string[])
          : [];

        if (Number(mm["quota_type"]) === 1) {
          // 按次计费：直接使用 model_price 显示一次性价格，倍率使用 - 代替
          const mp = mm["model_price"];
          const priceDisplay =
            typeof mp === "number" ? formatPrice(mp as number, locale, 4) : `-${String(mp ?? "-")}`;
          return {
            id,
            modelName,
            billingType: t("billingTypes.perUse"),
            inputPrice: priceDisplay,
            outputPrice: "-",
            inputRate: "-",
            outputRate: "-",
            group,
            fallbackModel,
            description,
            cachePromptPrice,
            cacheCreationPrice,
            supportedEndpointTypes,
          };
        } else {
          // 按量计费：提示价格=2 * model_ratio，补全价格 = 提示价格 * completion_ratio
          const ratio = Number(mm["model_ratio"]) || 0;
          const completionRatio = Number(mm["completion_ratio"]) || 0;
          const promptPrice = 2 * ratio;
          const completionPrice = promptPrice * completionRatio;
          return {
            id,
            modelName,
            billingType: t("billingTypes.perToken"),
            inputPrice: formatPrice(promptPrice, locale, 6),
            outputPrice: formatPrice(completionPrice, locale, 6),
            inputRate: ratio === 0 ? "-" : ratio.toFixed(5),
            outputRate: completionRatio === 0 ? "-" : completionRatio.toFixed(5),
            group,
            fallbackModel,
            description,
            cachePromptPrice,
            cacheCreationPrice,
            supportedEndpointTypes,
          };
        }
      });

      setBillingItems(items);
      toast.success(t("messages.updateSuccess"));
    } catch (err: unknown) {
      if (err instanceof Error) {
        logger.error("fetchPricing 失败:", err);
      } else {
        logger.error("fetchPricing 失败:", String(err));
      }
      toast.error(t("messages.refreshFailed"));
    } finally {
      setIsRefreshing(false);
      setShowRefreshModal(false);
    }
  };

  // 刷新数据处理（使用 fetchPricing）
  const handleRefresh = async () => {
    await fetchPricing();
  };

  // 初始加载
  useEffect(() => {
    fetchPricing();
  }, []);

  return (
    <div className="w-full min-h-screen p-3 flex justify-center">
      <div className="w-full space-y-6">
        <div className="mb-6 text-[13px]">
          <h1 className="text-3xl mb-2 text-foreground font-bold">{t("title")}</h1>
          <Alert className="mb-3 bg-green-50/50 dark:bg-green-900/10 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300">
            <CircleCheck size={16} />
            <AlertDescription>{t("groupRate")}:1.00</AlertDescription>
          </Alert>
          <Alert className="mb-3 bg-primary/5 dark:bg-primary/10 border-primary/30 text-primary">
            <CircleAlert size={16} />
            <AlertDescription>{t("conversionFormula")}</AlertDescription>
          </Alert>
          <Alert className="mb-3 bg-primary/5 dark:bg-primary/10 border-primary/30 text-primary">
            <CircleAlert size={16} />
            <AlertDescription>
              {t("billingFormula")}
            </AlertDescription>
          </Alert>

          <div className="flex items-center gap-3 mt-4">
            <div className="flex-1 relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                type="text"
                placeholder={t("searchPlaceholder")}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              onClick={() => setShowRefreshModal(true)}
              className="px-4 gap-2"
            >
              <RefreshCw size={18} />
              {t("refresh")}
            </Button>
          </div>
        </div>

        <Card className="rounded-2xl shadow-sm bg-card">
          <CardHeader>
            <CardTitle>{t("feeDescription")}</CardTitle>
          </CardHeader>
          <CardContent>

            {/* 表格 */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="text-muted-foreground">
                    <TableHead className="w-[28%]">{t("tableHeaders.modelName")}</TableHead>
                    <TableHead className="w-[8%]">{t("tableHeaders.billingType")}</TableHead>
                    <TableHead className="w-[20%]">{t("tableHeaders.price")}</TableHead>
                    <TableHead className="w-[14%]">{t("tableHeaders.rate")}</TableHead>
                    <TableHead className="w-[8%]">{t("tableHeaders.group")}</TableHead>
                    <TableHead className="w-[22%]">{t("tableHeaders.supportedEndpoints")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        {searchQuery ? t("noMatch") : t("noData")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map(item => (
                      <React.Fragment key={item.id}>
                      <TableRow
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleRow(item.id)}
                      >
                        <TableCell className="text-foreground">
                          <div className="flex items-center gap-1">
                            {expandedRows.has(item.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            {item.modelName}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="destructive" className="text-[11px]">
                            {item.billingType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] text-muted-foreground">
                                {item.billingType === t("billingTypes.perToken")
                                  ? t("price.promptPerMToken") + ":"
                                  : t("price.promptPerUse") + ":"}
                              </span>
                              <Badge variant="outline" className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-[11px] border-0">
                                {item.inputPrice}
                              </Badge>
                            </div>
                            {item.billingType !== t("billingTypes.perUse") && (
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-muted-foreground">
                                  {t("price.completionPerMToken")}:
                                </span>
                                <Badge variant="outline" className="bg-primary/10 dark:bg-primary/10 text-primary dark:text-primary text-[11px] border-0">
                                  {item.outputPrice}
                                </Badge>
                              </div>
                            )}
                            {item.cachePromptPrice !== "-" && (
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-muted-foreground">{t("price.cachePrice")}:</span>
                                <Badge variant="outline" className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[11px] border-0">
                                  {item.cachePromptPrice}
                                </Badge>
                              </div>
                            )}
                            {item.cacheCreationPrice !== "-" && (
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-muted-foreground">{t("price.cacheCreationPrice")}:</span>
                                <Badge variant="outline" className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[11px] border-0">
                                  {item.cacheCreationPrice}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] text-muted-foreground">
                                {item.billingType === t("billingTypes.perToken") ? t("rate.promptRate") + ":" : t("rate.promptRatePerUse") + ":"}
                              </span>
                              <Badge variant="outline" className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-[11px] border-0">
                                {item.inputRate}
                              </Badge>
                            </div>
                            {item.billingType !== t("billingTypes.perUse") && (
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-muted-foreground">{t("rate.completionRate")}:</span>
                                <Badge variant="outline" className="bg-primary/10 dark:bg-primary/10 text-primary dark:text-primary text-[11px] border-0">
                                  {item.outputRate}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-[11px] border-0">
                            {item.group}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {item.supportedEndpointTypes.length > 0 ? (
                              item.supportedEndpointTypes.map(ep => (
                                <Badge key={ep} variant="outline" className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[11px] border-0">
                                  {ep}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-muted-foreground text-[11px]">-</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedRows.has(item.id) && (
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={6} className="py-3 px-6">
                            <div className="grid grid-cols-2 gap-6 text-[12px]">
                              <div className="space-y-1">
                                <div>
                                  <span className="text-muted-foreground">{t("detail.fallbackModel")}:</span>{" "}
                                  <span className="text-foreground">{item.fallbackModel}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">{t("detail.description")}:</span>{" "}
                                  <span className="text-foreground">{item.description}</span>
                                </div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">{t("detail.endpointPaths")}:</span>
                                {item.supportedEndpointTypes.length > 0 ? (
                                  <div className="mt-1 space-y-1">
                                    {item.supportedEndpointTypes.map(ep => {
                                      const info = endpointMap[ep];
                                      if (!info) return null;
                                      const path = info.path.replace("{model}", item.modelName);
                                      return (
                                        <div key={ep} className="flex items-center gap-2">
                                          <Badge variant="outline" className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[11px] border-0">
                                            {ep}
                                          </Badge>
                                          <code className="text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                            {info.method} {path}
                                          </code>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-foreground ml-1">-</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 刷新确认弹窗 */}
      <Dialog open={showRefreshModal} onOpenChange={setShowRefreshModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("refreshModal.title")}</DialogTitle>
            <DialogDescription>{t("refreshModal.description")}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 sm:gap-3">
            <Button
              variant="outline"
              onClick={() => setShowRefreshModal(false)}
              disabled={isRefreshing}
            >
              {t("refreshModal.cancel")}
            </Button>
            <Button
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  {t("refreshModal.refreshing")}
                </>
              ) : (
                t("refreshModal.confirm")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
